import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApp } from '@/contexts/AppContext';
import ProductCard from '@/components/ProductCard';
import AuthenticatedBadge from '@/components/AuthenticatedBadge';
import { ArrowRight, BadgeCheck, Sparkles, Package } from 'lucide-react';

const HERO = 'https://images.pexels.com/photos/28262860/pexels-photo-28262860.jpeg';
const STORE = 'https://images.pexels.com/photos/37793325/pexels-photo-37793325.jpeg';
const ED1 = 'https://images.pexels.com/photos/9514677/pexels-photo-9514677.jpeg';
const ED2 = 'https://images.pexels.com/photos/28251082/pexels-photo-28251082.jpeg';

const CATEGORIES = [
  { name: 'Handbags', img: 'https://images.pexels.com/photos/14806252/pexels-photo-14806252.jpeg' },
  { name: 'Watches', img: 'https://images.pexels.com/photos/380782/pexels-photo-380782.jpeg' },
  { name: 'Jewelry', img: 'https://images.pexels.com/photos/4974506/pexels-photo-4974506.jpeg' },
  { name: 'Shoes', img: 'https://images.pexels.com/photos/267301/pexels-photo-267301.jpeg' },
];

export default function Home() {
  const { t, api } = useApp();
  const [products, setProducts] = useState([]);
  const [designers, setDesigners] = useState([]);
  useEffect(() => {
    api.get('/products?limit=8').then((r) => setProducts(r.data.items || []));
    api.get('/designers').then((r) => setDesigners((r.data || []).slice(0, 8)));
  }, [api]);

  return (
    <div data-testid="home-page">
      {/* HERO — Editorial split */}
      <section className="relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 min-h-[80vh]">
          <div className="lg:col-span-7 relative overflow-hidden">
            <img src={HERO} alt="Editorial" className="w-full h-full object-cover absolute inset-0" />
            <div className="absolute inset-0 bg-foreground/10" />
          </div>
          <div className="lg:col-span-5 flex flex-col justify-center px-8 md:px-14 py-16 bg-background">
            <span className="overline text-accent" data-testid="hero-eyebrow">{t('home.hero.eyebrow')}</span>
            <h1 className="mt-6 font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight whitespace-pre-line" data-testid="hero-title">
              {t('home.hero.title')}
            </h1>
            <p className="mt-8 text-base font-light leading-relaxed text-muted-foreground max-w-md">
              {t('home.hero.body')}
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Link to="/shop" className="inline-flex items-center justify-center gap-3 bg-foreground text-background px-8 py-4 overline hover:opacity-80 transition" data-testid="hero-shop-btn">
                {t('cta.shop')} <ArrowRight size={14} />
              </Link>
              <Link to="/sell" className="inline-flex items-center justify-center gap-3 border border-foreground px-8 py-4 overline hover:bg-foreground hover:text-background transition" data-testid="hero-sell-btn">
                {t('cta.sell')}
              </Link>
            </div>
            <div className="mt-12 grid grid-cols-3 gap-6 pt-8 border-t border-border">
              <div>
                <BadgeCheck size={20} strokeWidth={1.2} className="text-accent" />
                <div className="overline mt-3">Authenticated</div>
                <div className="text-xs text-muted-foreground mt-1 font-light">By gemologists & horologists</div>
              </div>
              <div>
                <Sparkles size={20} strokeWidth={1.2} className="text-accent" />
                <div className="overline mt-3">Curated</div>
                <div className="text-xs text-muted-foreground mt-1 font-light">200k+ pieces, all coveted</div>
              </div>
              <div>
                <Package size={20} strokeWidth={1.2} className="text-accent" />
                <div className="overline mt-3">Insured Shipping</div>
                <div className="text-xs text-muted-foreground mt-1 font-light">White-glove service</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Category mosaic */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-24">
        <div className="flex items-end justify-between mb-12">
          <h2 className="font-display text-3xl sm:text-5xl tracking-tight">Shop by Category</h2>
          <Link to="/shop" className="overline link-underline hidden sm:inline-block" data-testid="categories-view-all">{t('cta.viewAll')} →</Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {CATEGORIES.map((c) => (
            <Link key={c.name} to={`/shop?category=${c.name}`} className="group block" data-testid={`category-${c.name}`}>
              <div className="aspect-[3/4] overflow-hidden bg-muted">
                <img src={c.img} alt={c.name} className="w-full h-full object-cover ken-burns" />
              </div>
              <div className="mt-4 font-display text-xl">{c.name}</div>
            </Link>
          ))}
        </div>
      </section>

      {/* Curated arrivals */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-24 border-t border-border">
        <div className="flex items-end justify-between mb-12">
          <div>
            <span className="overline text-accent">New This Week</span>
            <h2 className="font-display text-3xl sm:text-5xl tracking-tight mt-2">{t('home.featured')}</h2>
          </div>
          <Link to="/shop" className="overline link-underline" data-testid="featured-view-all">{t('cta.viewAll')} →</Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-12">
          {products.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        </div>
      </section>

      {/* Consign block */}
      <section className="relative bg-secondary py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-6 aspect-[4/5] overflow-hidden">
            <img src={STORE} alt="Consign" className="w-full h-full object-cover" />
          </div>
          <div className="md:col-span-6">
            <span className="overline text-accent">For Sellers</span>
            <h2 className="font-display text-4xl sm:text-6xl mt-4 leading-none">{t('home.consign.title')}</h2>
            <p className="mt-6 text-muted-foreground font-light text-lg leading-relaxed max-w-lg">{t('home.consign.body')}</p>
            <div className="mt-10 grid grid-cols-3 gap-4 max-w-md">
              {['01 Submit', '02 Authenticate', '03 Earn'].map((s) => (
                <div key={s} className="border-t border-foreground pt-3">
                  <span className="overline">{s}</span>
                </div>
              ))}
            </div>
            <Link to="/sell" className="mt-10 inline-flex items-center gap-3 bg-foreground text-background px-8 py-4 overline hover:opacity-80 transition" data-testid="consign-cta">
              {t('cta.sell')} <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* Designers */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-24">
        <div className="flex items-end justify-between mb-12">
          <h2 className="font-display text-3xl sm:text-5xl tracking-tight">{t('home.designers')}</h2>
          <Link to="/designers" className="overline link-underline">{t('cta.viewAll')} →</Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-4">
          {designers.map((d) => (
            <Link key={d.name} to={`/shop?designer=${encodeURIComponent(d.name)}`} className="group flex items-baseline justify-between border-b border-border py-4" data-testid={`designer-${d.name}`}>
              <span className="font-display text-2xl group-hover:text-accent transition">{d.name}</span>
              <span className="overline text-muted-foreground">{d.count}</span>
            </Link>
          ))}
        </div>
      </section>

      {/* Editorial */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-24 border-t border-border">
        <span className="overline text-accent">{t('home.editorial')}</span>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mt-8">
          <Link to="/editorial" className="md:col-span-8 group" data-testid="editorial-1">
            <div className="aspect-[16/10] overflow-hidden bg-muted">
              <img src={ED1} alt="Editorial" className="w-full h-full object-cover ken-burns" />
            </div>
            <div className="mt-6">
              <span className="overline text-muted-foreground">Trend Report</span>
              <h3 className="font-display text-3xl sm:text-4xl mt-2 leading-tight">The Quiet Luxury Manifesto: Why Whispered Wealth is the New Loud</h3>
            </div>
          </Link>
          <Link to="/editorial" className="md:col-span-4 group" data-testid="editorial-2">
            <div className="aspect-[3/4] overflow-hidden bg-muted">
              <img src={ED2} alt="Editorial" className="w-full h-full object-cover ken-burns" />
            </div>
            <div className="mt-6">
              <span className="overline text-muted-foreground">Sustainability</span>
              <h3 className="font-display text-2xl mt-2 leading-tight">A Second Life for Couture: The Circular Future of Luxury</h3>
            </div>
          </Link>
        </div>
      </section>
    </div>
  );
}
